import { Component, EventEmitter, OnInit, Output, OnDestroy, AfterViewInit, ViewChildren, ViewChild, Renderer2 } from '@angular/core';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { map, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { RecaptchaSettings } from 'src/app/modules/shared/model/recaptchaSettings.model';
import { StringKeyValue } from 'src/app/modules/shared/model/stringKeyValue.model';
import { UserModel } from 'src/app/modules/shared/model/userModel.model';
import { addUserModels } from 'src/app/state/actions/loggedin-user.actions';
import { isStringNullOrWhiteSpace } from 'src/app/util/StringUtil';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { selectAppSettings } from 'src/app/state/selectors/app-settings.selectors';
import { CookieService } from 'ngx-cookie-service';
import { of, Subject } from 'rxjs';
import { UserManagementConst } from '../../model/user-management.const.model';
import { AdminSelectionEvent } from 'src/app/modules/shared/model/adminSelectionEvent';
import { AccelaControl } from 'src/app/modules/shared/model/accelaControl.model';
import { Dialog } from 'primeng/dialog';
import { LocalizationService } from 'src/app/modules/shared/services/localization.service';
import { AccountService } from 'src/app/modules/shared/services/account.service';
import { EncryptionService } from 'src/app/modules/shared/services/encryption.service';
import { NavigationService } from 'src/app/modules/shared/services/navigation-service.service';
import { PageLayoutService } from 'src/app/modules/shared/services/page-layout.service';
import { SettingService } from 'src/app/modules/shared/services/setting.service';
import { ActivatedRoute } from '@angular/router';
import { CustomScriptInjectionService } from 'src/app/modules/shared/services/custom-script-injection.service';

@Component({
  selector: 'aca-login-panel',
  templateUrl: './login-panel.component.html',
  styleUrls: ['./login-panel.component.css'],
})
export class LoginPanelComponent implements OnInit, OnDestroy {
  @Output('message') broadcastMessage = new EventEmitter<string>(true);
  @Output() selectionEvent = new EventEmitter<AdminSelectionEvent>();

  @ViewChild('adminOverlay') adminUpdatePanel: Dialog;

  adminMode: boolean;
  RecaptchaInfo: RecaptchaSettings;
  username: string;
  password: string;
  returnURL = '';
  moduleParam = '';
  rememberMe: boolean;
  recaptchaLoaded = false;
  recaptchaVerified = false;
  securityQuestionsRequired = false;
  showRegistrationLinks = false;
  displayAdminOverlay = false;
  processing = true;
  licenseRequiredForRegistration = false;
  userNameControlDto = {
    name: 'userNameControl'
  } as AccelaControl;
  passwordControlDto = {
    name: 'passwordControl'
  } as AccelaControl;
  rememeberMeDto = {
    name: 'rememberMeControl'
  } as AccelaControl;
  selectedDto =  this.userNameControlDto;
  adminSelectedControl = 'username';
  viewId = '1008';
  userNamePlaceHolder = '';
  passwordPlaceHolder = '';
  protected destroyActions = new Subject<boolean>();

  constructor(private apiRef: ApiProviderService, private accountService: AccountService, public settingService: SettingService,
    public pageLayoutService: PageLayoutService, public localizationService: LocalizationService, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2, public navigationService: NavigationService, private messageService: MessageService,
    private encryptionService: EncryptionService, private store: Store<AppState>, public cookieService: CookieService,
    private customScriptService: CustomScriptInjectionService) {

    if(!this.adminMode && this.cookieService.check(UserManagementConst.REMEBERME_COOKIE)) { //TODO: Make string const
      this.rememberMe = true;
      this.username = this.cookieService.get(UserManagementConst.REMEBERME_COOKIE);
    }

    this.settingService.isRegistrationEnabled()
      .pipe(takeUntil(this.destroyActions))
      .subscribe(r => this.showRegistrationLinks = r);

    this.settingService.getRecaptchaSettings()
      .pipe(takeUntil(this.destroyActions))
      .subscribe(res => {
        this.RecaptchaInfo = res;
      }
      , error => {
        this.messageService.add({
          severity: 'error',
          summary: error,
          closable: false
        });
      }      );
    this.renderReCaptcha(this);

    this.settingService.getLoginSettings()
      .pipe(takeUntil(this.destroyActions))
      .subscribe(res => {
        this.securityQuestionsRequired = res.Enable;
      }
      , error => {
        this.messageService.add({
          severity: 'error',
          summary: error,
          closable: false
        });
      });
    this.settingService.isLicenseRequiredForRegistration()
      .pipe(takeUntil(this.destroyActions))
      .subscribe(res => {
        this.licenseRequiredForRegistration = res;
      });
    this.loadDbLocalization();
  }

  ngOnInit(): void {
    this.settingService.isEnableCustomizationPerPage()
    .pipe(takeUntil(this.destroyActions))
    .subscribe(res => {
      if(res){
        this.customScriptService.loadAndRunCustomScriptBlock(this.renderer, document, 'login-panel');
      }
  });

    this.store.select(selectAppSettings)
      .pipe(takeUntil(this.destroyActions))
      .subscribe({
        next: state => {
          this.adminMode = state.AdminMode;
          //Resetting the remember me username on Admin mode
          if (this.adminMode) {
            this.username = '';
          }
        }});
    const urlParams = new URLSearchParams(this.apiRef.nativeWindow.location.search);

    //check for and store any ReturnUrl parameter
    const rUrlParam =  urlParams.get(UserManagementConst.RETURN_URL)?.trim();
    if(!isStringNullOrWhiteSpace(rUrlParam)){
      const url = this.apiRef.nativeWindow.location.search;
      this.returnURL = decodeURIComponent(url.substring(url.indexOf(UserManagementConst.RETURN_URL)+10));
    }

    //check for and store any module param
    this.moduleParam = urlParams.get('module')?.trim();
  }

  loadDbLocalization(): void {
    this.localizationService.getDefaultLabelByKey('acc_sign_label_username').subscribe(s => this.userNameControlDto.defaultLabel = s);
    this.localizationService.getLabelByKey('acc_sign_label_username').subscribe(s => this.userNameControlDto.label = s);
    this.localizationService.getHtmlText('acc_sign_label_username|sub', '').subscribe(s => this.userNameControlDto.tooltip = s);
    this.localizationService.getLabelByKey('acc_sign_label_username|watermark').subscribe(s => {
      this.userNameControlDto.placeholder = s;
      this.userNamePlaceHolder = s;
    });

    this.localizationService.getDefaultLabelByKey('acc_sign_label_password').subscribe(s => this.passwordControlDto.defaultLabel = s);
    this.localizationService.getLabelByKey('acc_sign_label_password').subscribe(s => this.passwordControlDto.label = s);
    this.localizationService.getHtmlText('acc_sign_label_password|sub', '').subscribe(s => this.passwordControlDto.tooltip = s);
    this.localizationService.getLabelByKey('acc_sign_label_password|watermark').subscribe(s => {
      this.passwordControlDto.placeholder = s;
      this.passwordPlaceHolder = s;
    });

    this.localizationService.getDefaultLabelByKey('acc_sign_label_rememberMe').subscribe(s => this.rememeberMeDto.defaultLabel = s);
    this.localizationService.getLabelByKey('acc_sign_label_rememberMe').subscribe(s => this.rememeberMeDto.label = s);
  }

  renderReCaptcha(ref): void {
    if (this.RecaptchaInfo && !this.RecaptchaInfo.IsCaptchaEnabledForLogin) {
      this.recaptchaLoaded = true;
      return;
    }

    if (!this.RecaptchaInfo || !(window as any).grecaptcha || !(window as any).grecaptcha.render) {
      setTimeout(()=> {
        ref.renderReCaptcha(ref);
      }, 400);

    } else {
      this.recaptchaLoaded = true;
      return;
    }
  }

  updateRecaptchaStatus(result: boolean): void {
    this.recaptchaVerified = result;
    if (result === true) {
      const box = document.getElementById('captcha');
      box.style.setProperty('border', 'none');
    }
    this.processing = false;
  }

  getForgotPasswordLink(): string {
    return 'Account/ForgotPassword.aspx'; // TODO add forgotten password screen
  }

  forgotPassword(event: MouseEvent): void {
    if (!this.adminMode) {
      this.navigationService.navigateLegacyPage(this.getForgotPasswordLink());
    }
  }

  login(event: MouseEvent): void {

    if (this.RecaptchaInfo.IsCaptchaEnabledForLogin && !this.recaptchaVerified) {
      this.messageService.clear();
      const box = document.getElementById('captcha');
      box.style.setProperty('border', '1px solid red');
      this.messageService.add({
        severity: 'error',
        summary: $localize`Please fill out the Recaptcha properly before submitting again.`,
        closable: false
      });
      return;
    }

    if (this.adminMode || (this.RecaptchaInfo.IsCaptchaEnabledForLogin && this.processing)) {
      return;
    }
    if(!this.RecaptchaInfo){
      console.log($localize `Unable to login. Still loading Recaptcha data`);
      return;
    }
    if(isStringNullOrWhiteSpace(this.username) || isStringNullOrWhiteSpace(this.password)){
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: $localize`Not all fields are filled out correctly. Please check again.`,
        closable: false
      });
      return;
    }
    this.processing = true;

    const userModel: UserModel = {
      Name: this.username,
      Pwd: this.password,
      IsRemember: this.rememberMe ? 1 : 0,
    };

    if(this.rememberMe){
      //expires 10 days from current date
      this.cookieService.set(UserManagementConst.REMEBERME_COOKIE, this.username, new Date().getDate() + 10);
    } else {
      this.cookieService.delete(UserManagementConst.REMEBERME_COOKIE);
    }

    if(this.securityQuestionsRequired){
      this.accountService.validateUserAccount(userModel)
        .pipe(takeUntil(this.destroyActions))
        .subscribe((resp => {
          if(resp.type === 'success'){
            this.navigateToSecurityQuestions(userModel);
          } else if(resp.type === 'redirect'){
            this.navigationService.navigate(this.apiRef.vDir + resp.redirectUrl);
          } else {
            this.messageService.clear();
            this.messageService.add({
              severity: 'error',
              summary: resp.message,
              closable: false
            });
          }
        }), error => {
          if (error['status'] !== 0) {
            this.messageService.add({
              severity: 'error',
              summary: error,
              closable: false
            });
          }
        });
    } else {
      this.accountService.loginToACA(userModel)
      .pipe(takeUntil(this.destroyActions))
      .subscribe((resp => {
        if(resp.forcePasswordChange){
          resp.url = UserManagementConst.CHANGE_PASSWORD_ROUTE + `&userID=${this.username}`;
          resp.redirectUrl = UserManagementConst.CHANGE_PASSWORD_ROUTE + `&userID=${this.username}`;
        }

        if(!isStringNullOrWhiteSpace(this.returnURL)){
          if(this.returnURL.includes('module')) {
            resp.url = this.returnURL; //legacy redirects need the module, even if blank
            resp.redirectUrl = this.returnURL;
          } else {
            const idx = this.returnURL.indexOf('?');
            resp.url = this.returnURL + ((idx < 0) ? '?' : '&') + `module=${this.moduleParam??''}`;
            resp.redirectUrl = this.returnURL + ((idx < 0) ? '?' : '&') + `module=${this.moduleParam??''}`;
          }
        } else {
          resp.url = this.apiRef.vDir + resp.url;
          resp.redirectUrl = this.apiRef.vDir + resp.redirectUrl;
        }

        if (resp.type === 'success') {
          this.navigationService.navigate(resp.redirectUrl);
        } else if (resp.type === 'redirect') {
          this.navigationService.navigate(resp.url);
        } else if (resp.type === 'error') {
          this.messageService.clear();
          this.messageService.add({
            severity: 'error',
            summary: resp.message,
            closable: false
          });
        }
      })
        , error => {
          if (error['status'] !== 0) {
            this.messageService.add({
              severity: 'error',
              summary: error,
              closable: false
            });
          }
      });

    }
    this.processing = false;

  }

  newUserRegistration(event: MouseEvent): void {
    if (!this.adminMode) {
      if(this.licenseRequiredForRegistration) {
        this.navigationService.navigateRoute('account/register-declare-license');
      } else {
        this.navigationService.navigateRoute('account/new');
      }
    }
  }

  navigateToSecurityQuestions(userModel): void {
    this.store.dispatch(addUserModels({ LoggedInUser: userModel }));
    const param = encodeURI(this.encryptionService.encryptObjRabbit(userModel));
    const kvp: Array<StringKeyValue> = [
      {
        Key: 'userData',
        Value: param
      },
      {
        Key: UserManagementConst.RETURN_URL,
        Value: encodeURIComponent(this.returnURL) ?? ''
      },
    ];
    if(this.activatedRoute.snapshot.queryParamMap.get('OpenCities')){
      kvp.unshift({
        Key: 'OpenCities',
        Value: 'true'
      });
    }

    if(!isStringNullOrWhiteSpace(this.moduleParam)){
      kvp.push({
        Key: 'module',
        Value: this.moduleParam??''
      });
    }
    this.navigationService.navigateRoute('login-questions', kvp);
  }

  adminSelection(e: Event, id: string): void {
    if(this.adminMode){
      this.adminSelectedControl = id;
      switch(this.adminSelectedControl){
        case 'username':
          this.selectedDto =  this.userNameControlDto;
          break;
        case 'passwordRequired':
          this.selectedDto =  this.passwordControlDto;
          break;
        case 'rememberMeControl':
          this.selectedDto =  this.rememeberMeDto;
          break;
      }

      if (!this.adminUpdatePanel.container) {
        this.adminUpdatePanel.container = this.adminUpdatePanel.el.nativeElement.firstElementChild;
      }
      this.displayAdminOverlay = true;
    }
  }

  submitAdminContentUpdate(saveDto: AccelaControl){
    let labelKey = '';
    switch(this.adminSelectedControl){
      case 'username':
        labelKey = 'acc_sign_label_username';
        break;
      case 'passwordRequired':
        labelKey = 'acc_sign_label_password';
        break;
    }

    const labelsToSave = [
      {Key: labelKey, Value: saveDto.label},
      {Key: `${labelKey}|sub`, Value: saveDto.tooltip},
      {Key: `${labelKey}|watermark`, Value: saveDto.placeholder},

    ];

    this.pageLayoutService.saveLabelArray(labelsToSave, this.viewId, '','','').subscribe(r=>console.log(r));
    this.loadDbLocalization();
    this.displayAdminOverlay = false;
  }

  ngOnDestroy(): void {
    this.customScriptService.removeTrackedElements();
    this.destroyActions.next();
    this.destroyActions.complete();
  }

}
